import { GAMES, SITE, type GameKind, type GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

/**
 * The catalogue, split by what the game is played with.
 *
 * Driven by data rather than written out as blocks of JSX, so a fourth kind —
 * dice, or whatever turns up — is an entry here and nothing else. That was the
 * claim made when there were two shelves, and adding the arcade one is what
 * tested it: it cost this entry, a motif below, and nothing else on the page.
 *
 * A shelf with nothing on it is not rendered at all, which is what keeps the
 * page honest while a shelf holds one game.
 */
const SHELVES: { kind: GameKind; title: string; note: string }[] = [
  {
    kind: 'cards',
    title: 'On the table',
    note: 'A real deck, a proper shuffle, and whatever the house rules say tonight.',
  },
  {
    kind: 'paper',
    title: 'On the beer mats',
    note: 'No deck and nothing to shuffle. A grid, two marks, and someone to beat.',
  },
  {
    kind: 'arcade',
    title: 'In the corner',
    note: 'The machine by the fruities. It knows something you do not, and it is not telling.',
  },
];

/**
 * The motif on a catalogue card: what the game is played with, at a glance.
 *
 * Four suit pips on a game with no deck in it was the first thing that looked
 * wrong when the catalogue stopped being all cards, and it would have looked
 * wrong a second time on a game with no opponent. Keyed by kind rather than
 * branched on, so the next shelf brings its own and this stays four lines.
 */
const MOTIFS: Record<GameKind, { glyph: string; tone: string }[]> = {
  cards: [
    { glyph: '♠', tone: 'is-black' },
    { glyph: '♥', tone: 'is-red' },
    { glyph: '♦', tone: 'is-red' },
    { glyph: '♣', tone: 'is-black' },
  ],
  paper: [
    { glyph: '✕', tone: 'is-cross' },
    { glyph: '◯', tone: 'is-nought' },
    { glyph: '✕', tone: 'is-cross' },
    { glyph: '◯', tone: 'is-nought' },
  ],
  // A d-pad, which is the one piece of equipment every machine in the corner
  // has and no beer mat ever did.
  arcade: [
    { glyph: '◄', tone: 'is-key' },
    { glyph: '▲', tone: 'is-key' },
    { glyph: '▼', tone: 'is-key' },
    { glyph: '►', tone: 'is-key' },
  ],
};

function GameCard({ game }: { game: GameSummary }) {
  const available = game.status === 'available';

  return (
    <article className={`gamecard gamecard--${game.kind} ${available ? '' : 'gamecard--soon'}`}>
      {/* Real text rather than decoration, so it is announced with the card
          and not just seen. First in the DOM so it reads "Beta, FreeCell…". */}
      {game.beta && (
        <span className="gamecard__flag" title={`${game.name} is new — expect the odd rough edge`}>
          <span className="gamecard__flag-band">Beta</span>
        </span>
      )}

      {/* See MOTIFS: what the game is played with, at a glance. */}
      <div className={`gamecard__motif gamecard__motif--${game.kind}`} aria-hidden="true">
        {MOTIFS[game.kind].map((mark, index) => (
          <span key={index} className={mark.tone}>
            {mark.glyph}
          </span>
        ))}
      </div>

      <h3 className="gamecard__name">{game.name}</h3>
      <p className="gamecard__blurb">{game.blurb}</p>

      {/* A one-seat game has no opponents and no range, and saying otherwise on
          the front page is a small lie about what the game is. */}
      <dl className="gamecard__facts">
        <div>
          <dt>Players</dt>
          <dd>
            {game.minPlayers === game.maxPlayers
              ? game.minPlayers
              : `${game.minPlayers}–${game.maxPlayers}`}
          </dd>
        </div>
        <div>
          <dt>Opponents</dt>
          <dd>{game.maxPlayers > 1 ? 'People or AI' : 'Just you'}</dd>
        </div>
      </dl>

      {available ? (
        <a className="btn btn--primary gamecard__action" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      ) : (
        <span className="gamecard__soon-tag">Coming soon</span>
      )}
    </article>
  );
}

export function SiteHome() {
  return (
    <main className="page">
      <section className="hero">
        <h1 className="hero__title">{SITE.tagline}</h1>
        <p className="hero__blurb">{SITE.blurb}</p>
      </section>

      {SHELVES.map((shelf) => {
        const games = GAMES.filter((game) => game.kind === shelf.kind);
        if (games.length === 0) return null;

        return (
          <section key={shelf.kind} className="catalogue" aria-label={shelf.title}>
            <h2 className="section__title">
              <span>{shelf.title}</span>
            </h2>
            <p className="catalogue__note">{shelf.note}</p>
            <div className="catalogue__grid">
              {games.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </section>
        );
      })}

      <section className="howitworks" aria-label="How it works">
        <h2 className="section__title">
          <span>How it works</span>
        </h2>
        <ol className="steps">
          <li className="step">
            <span className="step__num">1</span>
            <div>
              <h3>Pick a name</h3>
              <p>No account, no email, no password. Just what you want to be called.</p>
            </div>
          </li>
          <li className="step">
            <span className="step__num">2</span>
            <div>
              <h3>Share the code</h3>
              <p>Every table gets a six-character code. Send it to whoever you want at it.</p>
            </div>
          </li>
          <li className="step">
            <span className="step__num">3</span>
            <div>
              <h3>Play</h3>
              <p>Fill any empty seats with AI, set the house rules, and away you go.</p>
            </div>
          </li>
        </ol>
      </section>
    </main>
  );
}
